import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import smoothscroll from 'smoothscroll-polyfill';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useSelector } from 'react-redux';
import scrollbarVisible from '../functions/scrollbarVisible';

const PROGRESS_HEIGHT = 290;
smoothscroll.polyfill();

const useStyles = makeStyles((theme) => ({
  root: ({ embedded }) => ({
    backgroundColor: '#fff',
    borderRadius: 5,
    padding: '0px 20px',
    margin: '10px auto',
    maxWidth: 893,
    height: embedded ? '85% !important' : 'unset',
    minHeight: '70vh',
    flexWrap: 'nowrap',
    flexDirection: 'column',
    overflowY: 'auto !important',
    [theme.breakpoints.down('sm')]: {
      height: embedded ? '85% !important' : 'calc(100% - 62px) !important',
      minHeight: 'unset',
      overflowY: 'auto important',
      marginBottom: 36,
      paddingBottom: embedded ? 10 : 100,
    },
  }),
  providerRootQuestionContainer: {
    backgroundColor: '#fff',
    borderRadius: 5,
    padding: '0px 20px',
    margin: '10px auto 0',
    minHeight: 'unset',
    flexWrap: 'nowrap',
    flexDirection: 'column',
    overflowY: 'auto !important',
    height: 'calc(100% - 62px) !important',
    [theme.breakpoints.down('sm')]: {
      minHeight: 'unset',
      overflowY: 'auto important',
      marginBottom: 0,
      paddingBottom: 0,
    },
  },
}));

const QuestionContainer = (props) => {
  const {
    className, children, isHeightResizeable, questionId, embedded,
  } = props;
  const [viewportHeight, setViewportHeight] = React.useState(window.innerHeight - PROGRESS_HEIGHT);
  const { appConfiguration: { providerSpecific } = {} } = useSelector((state) => state) ?? {};

  const classes = useStyles({ embedded });
  const ref = React.useRef();

  const rootStyle = isHeightResizeable && !providerSpecific?.code && !embedded
    ? { height: viewportHeight }
    : undefined;

  React.useEffect(() => {
    if (!isHeightResizeable) {
      return () => {};
    }

    const resizeHandler = () => {
      setViewportHeight(window.innerHeight - PROGRESS_HEIGHT);
    };

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [isHeightResizeable]);

  React.useEffect(() => {
    const isScrollbarVisible = scrollbarVisible(ref.current);

    if (!isHeightResizeable || !ref.current || !isScrollbarVisible) {
      return;
    }

    // Scroll to the top if scrollbar exists
    ref.current.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [isHeightResizeable, questionId]);

  const isProvider = !!providerSpecific?.code;

  // unmounting (children is not rendered) causes side effects in rendered question (e.g. children)
  return (
    <Grid
      container
      className={cx(
        className,
        { [classes.providerRootQuestionContainer]: isProvider, [classes.root]: !isProvider },
      )}
      style={rootStyle}
      ref={ref}
    >
      {children}
    </Grid>
  );
};

QuestionContainer.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  isHeightResizeable: PropTypes.bool,
  className: PropTypes.string,
  questionId: PropTypes.string,
  embedded: PropTypes.bool,
};

QuestionContainer.defaultProps = {
  className: '',
  isHeightResizeable: false,
  questionId: 'id',
};

export default QuestionContainer;
